import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { decodeJWT } from '../utils/auth';
import { PLANBASE_CONFIG } from '../config';
import { Button } from '../components/ui/button';

import { CheckCircleIcon, ClockIcon, Cog6ToothIcon, EyeIcon, FolderIcon, PencilSquareIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';

interface ProjectSubmission {
  id: string;
  database_id: string;
  data: {
    title: string;
    description: string;
    category?: string;
    style?: string;
    location?: string;
    year?: string;
    architect?: string;
    coverImage?: string;
    images?: string[];
    tags?: string[];
    status?: 'draft' | 'pending' | 'published' | 'archived';
    created_by?: string;
  };
  status: 'draft' | 'pending' | 'published' | 'archived';
  slug?: string;
  created_at: string;
  updated_at: string;
  created_by: string;
}

export default function Portfolio() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [submissions, setSubmissions] = useState<ProjectSubmission[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);

  // Check if user has a portfolio setup
  const hasPortfolioSetup = user?.metadata?.username;

  // Delete project function
  const handleDeleteProject = async (projectId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!confirm('Are you sure you want to delete this project? This action cannot be undone.')) return;
    
    setDeleting(projectId);
    try {
      const token = localStorage.getItem('planbase_token');
      if (!token) throw new Error('Not authenticated');
      
      const payload = decodeJWT(token);
      const userId = payload?.sub || payload?.user_id || payload?.id;

      const response = await fetch(
        `${PLANBASE_CONFIG.apiUrl}/sdk/cms/records/${projectId}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${PLANBASE_CONFIG.apiKey}`,
            'X-Project-ID': PLANBASE_CONFIG.projectId,
            'X-User-ID': userId,
          },
        }
      );

      if (response.ok) {
        setSubmissions(prev => prev.filter(s => s.id !== projectId));
      } else {
        throw new Error('Failed to delete project');
      }
    } catch (err) {
      console.error('[Portfolio] Delete error:', err);
      alert('Failed to delete project. Please try again.');
    } finally {
      setDeleting(null);
    }
  };

  // Fetch user's projects. Uses the dedicated /me/projects endpoint so
  // the server resolves ownership from the authenticated session, not
  // a user id the frontend passed in — the prior ?created_by=<id>
  // flow silently returned nothing whenever the JWT's sub didn't
  // exactly match projects.owner_id (common on migrated rows).
  useEffect(() => {
    const fetchSubmissions = async () => {
      try {
        const token = localStorage.getItem('planbase_token');
        if (!token) {
          setError('Please log in to view your projects');
          setLoading(false);
          return;
        }
        const response = await fetch(
          `${PLANBASE_CONFIG.apiUrl}/sdk/planbase/me/projects?status=draft,pending,published,archived`,
          {
            headers: {
              'Authorization': `Bearer ${PLANBASE_CONFIG.apiKey}`,
              'X-Project-ID': PLANBASE_CONFIG.projectId,
              'X-User-Token': token,
            },
          }
        );
        if (!response.ok) {
          console.error('[Portfolio] /me/projects responded', response.status);
          setError('Failed to load your projects');
          return;
        }
        const data = await response.json();
        setSubmissions((data.records || []) as ProjectSubmission[]);
      } catch (err: any) {
        console.error('[Portfolio] Error:', err);
        setError('Failed to load your projects');
      } finally {
        setLoading(false);
      }
    };

    fetchSubmissions();
  }, []);

  if (loading) {
    return (
      <div className="space-y-6">
        {/* Header Skeleton */}
        <div className="flex items-center justify-between">
          <div>
            <div className="h-9 w-48 bg-muted rounded animate-pulse mb-2" />
            <div className="h-5 w-64 bg-muted rounded animate-pulse" />
          </div>
          <div className="h-10 w-32 md:w-40 bg-muted rounded-lg animate-pulse" />
        </div>

        {/* Projects Grid Skeleton */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="group">
              {/* Image Skeleton */}
              <div className="relative aspect-[4/3] overflow-hidden bg-muted rounded-xl mb-3 animate-pulse">
                {/* Status Badge Skeleton */}
                <div className="absolute top-3 right-3 h-8 w-24 bg-secondary rounded-full animate-pulse" />
              </div>

              {/* Project Info Skeleton */}
              <div className="space-y-2">
                <div className="flex items-start justify-between gap-2">
                  <div className="h-5 bg-muted rounded animate-pulse flex-1" />
                  <div className="h-8 w-8 bg-muted rounded-lg animate-pulse" />
                </div>
                <div className="h-4 bg-muted rounded animate-pulse w-full" />
                <div className="h-4 bg-muted rounded animate-pulse w-3/4" />
                <div className="flex items-center gap-2">
                  <div className="h-3 w-20 bg-muted rounded animate-pulse" />
                  <div className="h-3 w-12 bg-muted rounded animate-pulse" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Mobile sticky header */}
      <div className="md:hidden sticky top-0 z-20 bg-background/95 backdrop-blur-sm -mx-3 px-3 pt-3 pb-2 border-b border-border">
        <div className="flex items-center justify-between mb-1">
          <h1 className="text-sm font-semibold text-foreground uppercase tracking-wide">My Projects</h1>
          <div className="flex items-center gap-2">
            <button
              onClick={() => hasPortfolioSetup ? window.open(`/portfolio/${user?.metadata?.username}`, '_blank') : navigate('/portfolio-setup')}
              className="w-8 h-8 flex items-center justify-center rounded-full border border-border bg-background active:scale-95 transition-transform"
              aria-label="View portfolio"
            >
              {hasPortfolioSetup ? <EyeIcon className="h-4 w-4 text-muted-foreground" /> : <Cog6ToothIcon className="h-4 w-4 text-muted-foreground" />}
            </button>
            <button
              onClick={() => navigate('/portfolio/new')}
              className="w-8 h-8 flex items-center justify-center rounded-full bg-primary text-white active:scale-95 transition-transform"
              aria-label="New project"
            >
              <PlusIcon className="h-4 w-4" />
            </button>
          </div>
        </div>
        <p className="text-xs text-muted-foreground">
          {submissions.length} {submissions.length === 1 ? 'project' : 'projects'}
          {submissions.filter(s => (s.data?.status || s.status) === 'published').length > 0 &&
            ` · ${submissions.filter(s => (s.data?.status || s.status) === 'published').length} published`}
        </p>
      </div>

      {/* Desktop header */}
      <div className="hidden md:flex items-center justify-between">
        <div>
          <h1 className="text-sm font-semibold text-foreground uppercase tracking-wide">My Projects</h1>
          <p className="text-sm text-muted-foreground mt-0.5 font-normal">
            {submissions.length} {submissions.length === 1 ? 'project' : 'projects'}
            {submissions.filter(s => (s.data?.status || s.status) === 'published').length > 0 &&
              ` · ${submissions.filter(s => (s.data?.status || s.status) === 'published').length} published`}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => hasPortfolioSetup ? window.open(`/portfolio/${user?.metadata?.username}`, '_blank') : navigate('/portfolio-setup')}>
            {hasPortfolioSetup ? <><EyeIcon className="h-4 w-4 mr-1.5" /><span className="hidden sm:inline">View Portfolio</span></> : <><Cog6ToothIcon className="h-4 w-4 mr-1.5" /><span className="hidden sm:inline">Setup Portfolio</span></>}
          </Button>
          <Button onClick={() => navigate('/portfolio/new')} className="bg-primary hover:bg-primary/90 text-white">
            <PlusIcon className="h-4 w-4 mr-1.5" />
            <span className="hidden sm:inline">New Project</span>
            <span className="sm:hidden">New</span>
          </Button>
        </div>
      </div>

      {error && (
        <div className="border border-red-200 bg-red-50 rounded-xl p-4">
          <p className="text-red-600 text-sm">{error}</p>
        </div>
      )}

      {/* Projects */}
      {submissions.length === 0 ? (
        <div className="text-center py-20">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-muted mb-4">
            <FolderIcon className="h-8 w-8 text-muted-foreground" />
          </div>
          <h3 className="text-base font-medium text-foreground mb-2">No projects yet</h3>
          <p className="text-muted-foreground text-sm mb-6">Start building your portfolio</p>
          <Button onClick={() => navigate('/portfolio/new')} className="bg-primary hover:bg-primary/90 text-white">
            <PlusIcon className="h-4 w-4 mr-1.5" />Add Your First Project
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-6">
          {submissions.map((submission) => {
            const status = submission.data?.status || submission.status;
            return (
              <div
                key={submission.id}
                className="group cursor-pointer"
                onClick={() => navigate(`/project/${submission.slug || submission.id}`)}
              >
                {/* Image */}
                <div className="relative aspect-[4/3] overflow-hidden bg-muted rounded-xl mb-2 md:mb-3">
                  <img
                    src={submission.data.coverImage || 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=800&h=600&fit=crop'}
                    alt={submission.data.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  {/* Status badge */}
                  <div className="absolute top-2 left-2">
                    {status === 'published' && (
                      <span className="inline-flex items-center gap-0.5 px-2 py-0.5 bg-green-500/90 text-white rounded-full text-[10px] font-medium backdrop-blur-sm">
                        <CheckCircleIcon className="h-2.5 w-2.5" /> Live
                      </span>
                    )}
                    {status === 'pending' && (
                      <span className="inline-flex items-center gap-0.5 px-2 py-0.5 bg-yellow-500/90 text-white rounded-full text-[10px] font-medium backdrop-blur-sm">
                        <ClockIcon className="h-2.5 w-2.5" /> Review
                      </span>
                    )}
                    {status === 'draft' && (
                      <span className="inline-flex items-center gap-0.5 px-2 py-0.5 bg-black/50 text-white rounded-full text-[10px] font-medium backdrop-blur-sm">
                        <PencilSquareIcon className="h-2.5 w-2.5" /> Draft
                      </span>
                    )}
                  </div>
                  {/* Action buttons — show on hover desktop, always visible on mobile */}
                  <div className="absolute top-2 right-2 flex gap-1.5 md:opacity-0 md:group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={(e) => { e.stopPropagation(); navigate(`/edit-project/${submission.id}`); }}
                      className="w-7 h-7 flex items-center justify-center rounded-full bg-black/50 text-white backdrop-blur-sm active:scale-95 transition-transform"
                      aria-label="Edit"
                    >
                      <PencilSquareIcon className="h-3.5 w-3.5" />
                    </button>
                    <button
                      onClick={(e) => handleDeleteProject(submission.id, e)}
                      disabled={deleting === submission.id}
                      className="w-7 h-7 flex items-center justify-center rounded-full bg-black/50 text-white backdrop-blur-sm active:scale-95 transition-transform disabled:opacity-50"
                      aria-label="Delete"
                    >
                      <TrashIcon className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>

                {/* Info */}
                <div>
                  <p className="text-xs font-medium text-foreground line-clamp-1 group-hover:text-primary transition-colors leading-snug">
                    {submission.data.title}
                  </p>
                  <p className="text-[10px] text-muted-foreground mt-0.5">
                    {[submission.data.category, submission.data.year].filter(Boolean).join(' · ')}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
